/**
 * 74.11 (b) (DECISIONS 373; ARCHITECTURE §5.48): the fronts and the arrivals as the maps show them — the corner minimap and
 * the planet table draw the live weather cells as soft discs with a drift arrow, and the minimap's forecast line reads the
 * arrivals (core/weatherCells.ts frontArrivals) in words. Pure: no canvas, no three.
 */
import type { GameState, Vec3 } from '../types';
import type { WeatherKind } from '../core/weather';
import { cellsAt, weatherTime } from '../core/windField';
import { AHEAD_S } from './groundHudView';
import { type FrontArrival } from '../core/weatherCells';

/** A front on a map: its centre now and `AHEAD_S` later (the drift), its radius (rad of arc, and in people), its kind, how far it has eased in. */
export interface FrontMark { /** the cell's id (stable for its life) */ id: number; centre: Vec3; to: Vec3; radius: number; radiusPeople: number; kind: WeatherKind; ease: number }

/** The fronts over `planetIndex` at `seconds` (the cells on and no fixed window; else none), with where each stands ten seconds on. */
export function frontsOf(state: Pick<GameState, 'config' | 'turnNumber' | 'planets'>, planetIndex: number, seconds: number, person: number): FrontMark[] {
  if (!state.config.weather || !state.config.weatherCells || state.config.weatherFixed) return [];
  const planet = state.planets[planetIndex];
  if (!planet) return [];
  const now = cellsAt(state, planetIndex, weatherTime(state, seconds)), soon = cellsAt(state, planetIndex, weatherTime(state, seconds + AHEAD_S));
  return now.filter(c => c.kind !== 'clear' && c.ease > 0.05).map(c => ({ id: c.id ?? -1, centre: c.centre, to: soon.find(s => s.id === c.id)?.centre ?? c.centre, radius: c.radius, radiusPeople: c.radius * planet.radius / person, kind: c.kind, ease: c.ease }));
}

/** A kind's colour on a map (a soft fill at `alpha`). */
export function frontColor(kind: WeatherKind, alpha: number): string {
  const rgb: Record<WeatherKind, string> = { clear: '200,200,200', haze: '210,200,170', rain: '110,140,200', snow: '225,232,245', fog: '215,220,225', storm: '80,90,120', dust: '205,165,95', ash: '110,105,100' };
  return `rgba(${rgb[kind] ?? '200,200,200'},${alpha})`;
}

const WORDS: Record<string, string> = { rain: 'rain', snow: 'snow', storm: 'a storm', fog: 'a fog bank', dust: 'a dust front', ash: 'ash', haze: 'haze' };
/** "in 4 min" / "in 40 s" / "now". */
export function whenText(seconds: number): string { return seconds < 15 ? 'now' : seconds < 60 ? `in ${Math.round(seconds / 5) * 5} s` : `in ${Math.round(seconds / 30) / 2} min`; }
/** The minimap's weather line with the cells on: what is here, then the fronts due — "here clear · rain from the NW in 4 min · clearing in 11 min". `bearingOf` gives a front's compass arrow in the map's own frame. */
export function arrivalsText(here: WeatherKind | null, arrivals: readonly FrontArrival[], bearingOf: (a: FrontArrival) => string): string {
  const parts: string[] = [];
  if (here) parts.push(`here ${here}`);
  for (const a of arrivals) parts.push(a.kind === 'clear' ? `clearing ${whenText(a.seconds)}` : `${WORDS[a.kind] ?? a.kind} ${bearingOf(a)}${whenText(a.seconds)}`);
  return parts.join(' · ');
}
